import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { getMeetingById, getMeetingParticipants, confirmParticipation, subscribeToMeetingParticipants, subscribeToMeetingStatus } from '../../services/meetingService';
import Header from '../../components/ui/Header';
import StepProgressIndicator from '../../components/ui/StepProgressIndicator';
import WorkflowBreadcrumb from '../../components/ui/WorkflowBreadcrumb';
import NavigationActions from '../../components/ui/NavigationActions';
import LocationDetails from './components/LocationDetails';
import ParticipantTravelDetails from './components/ParticipantTravelDetails';
import BookingActions from './components/BookingActions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { trackMeetingConfirmed, trackMeetingStatusChange } from '../../utils/analytics';
import { validateDateFormat, validateTimeFormat, validateLocationName, validateLocationAddress, validateFieldType } from '../../utils/validation';
import { trackValidationError, trackSubmissionError } from '../../utils/errorTracking';

const MeetingConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  
  // Try to get meeting ID from query params first, then from state
  const meetingIdFromParams = searchParams?.get('meetingId');
  const meetingIdFromState = location?.state?.meetingId;
  const meetingId = meetingIdFromParams || meetingIdFromState;

  const [meeting, setMeeting] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [loadTimeout, setLoadTimeout] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/signin');
      return;
    }

    if (!meetingId) {
      setError('No meeting ID provided');
      setLoading(false);
      return;
    }

    // Set 10-second timeout for loading
    const timeoutId = setTimeout(() => {
      if (loading) {
        setLoadTimeout(true);
        setLoading(false);
        setError('Loading timed out after 10 seconds. Please try again.');
      }
    }, 10000);

    loadMeetingData();

    return () => clearTimeout(timeoutId);
  }, [meetingId, user, authLoading, navigate]);

  useEffect(() => {
    if (!meetingId) return;

    const unsubscribeParticipants = subscribeToMeetingParticipants(meetingId, () => {
      loadParticipants();
    });

    const unsubscribeStatus = subscribeToMeetingStatus(meetingId, (payload) => {
      if (payload?.new?.status) {
        setMeeting(prev => prev ? { ...prev, status: payload?.new?.status } : null);
        
        // Show notification when meeting is confirmed
        if (payload?.new?.status === 'confirmed' && payload?.old?.status === 'pending') {
          toast?.success('🎉 All participants have confirmed! Meeting is now active.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          });
          
          // Trigger email/SMS notifications
          triggerNotifications(meetingId);
        }
      }
    });

    return () => {
      unsubscribeParticipants();
      unsubscribeStatus();
    };
  }, [meetingId]);

  const loadMeetingData = async () => {
    try {
      setLoading(true);
      setError('');
      setLoadTimeout(false);
      const [meetingData, participantsData] = await Promise.all([
        getMeetingById(meetingId),
        getMeetingParticipants(meetingId)
      ]);

      if (!meetingData) {
        setError('Meeting not found');
        trackSubmissionError('meeting_not_found', 'Meeting data could not be loaded', { meetingId });
        return;
      }

      // Validate meeting data
      const errors = validateMeetingData(meetingData);
      if (Object.keys(errors)?.length > 0) {
        setValidationErrors(errors);
        console.warn('Meeting data validation warnings:', errors);
      }

      setMeeting(meetingData);
      setParticipants(participantsData || []);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to load meeting';
      setError(errorMessage);
      trackSubmissionError('load_meeting_error', errorMessage, { meetingId });
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async () => {
    try {
      const participantsData = await getMeetingParticipants(meetingId);
      setParticipants(participantsData || []);
      
      // Show toast notification when new participant joins
      if (participantsData?.length > participants?.length) {
        const newParticipant = participantsData?.[participantsData?.length - 1];
        toast?.info(`${newParticipant?.participantName} has joined the meeting!`, {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (err) {
      console.error('Failed to reload participants:', err);
    }
  };

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      setError('');
      
      // Validate before confirming
      if (Object.keys(validationErrors)?.length > 0) {
        toast?.error('Please fix validation errors before confirming', {
          position: 'top-right',
          autoClose: 3000
        });
        return;
      }

      // Set 10-second timeout for confirmation
      const confirmTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Confirmation timed out after 10 seconds')), 10000)
      );

      await Promise.race([
        confirmParticipation(meetingId),
        confirmTimeout
      ]);
      
      // Track confirmation
      trackMeetingConfirmed(meetingId, user?.email?.split('@')?.[0] || 'User');
      
      toast?.success('Participation confirmed successfully!', {
        position: 'top-right',
        autoClose: 3000
      });
    } catch (err) {
      const errorMessage = err?.message || 'Failed to confirm participation';
      setError(errorMessage);
      trackSubmissionError('confirm_participation_error', errorMessage, { meetingId, userId: user?.id });
      
      toast?.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000
      });
    } finally {
      setConfirming(false);
    }
  };

  const handleRetryLoad = () => {
    setError('');
    setLoadTimeout(false);
    loadMeetingData();
  };

  const triggerNotifications = async (meetingId) => {
    try {
      // Track status change to confirmed
      trackMeetingStatusChange(meetingId, 'pending', 'confirmed');
      
      // Call email notification edge function
      const emailResponse = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-meeting-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ meetingId, type: 'all_confirmed' })
        }
      );

      if (!emailResponse?.ok) {
        throw new Error('Email notification failed');
      }

      // Call SMS notification edge function
      const smsResponse = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-meeting-sms`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ meetingId, type: 'all_confirmed' })
        }
      );

      if (!smsResponse?.ok) {
        throw new Error('SMS notification failed');
      }

      console.log('Notifications triggered successfully');
    } catch (error) {
      console.error('Failed to trigger notifications:', error);
      trackSubmissionError('notification_error', error?.message || 'Notification failed', { meetingId });
    }
  };

  const validateMeetingData = (meetingData) => {
    const errors = {};

    // Validate date format
    const dateValidation = validateDateFormat(meetingData?.meetingDate);
    if (!dateValidation?.valid) {
      errors.meetingDate = dateValidation?.error;
      trackValidationError('meetingDate', dateValidation?.error, 'meeting-confirmation');
    }

    // Validate time format
    const timeValidation = validateTimeFormat(meetingData?.meetingTime);
    if (!timeValidation?.valid) {
      errors.meetingTime = timeValidation?.error;
      trackValidationError('meetingTime', timeValidation?.error, 'meeting-confirmation');
    }

    // Validate location name
    const nameValidation = validateLocationName(meetingData?.locationName);
    if (!nameValidation?.valid) {
      errors.locationName = nameValidation?.error;
      trackValidationError('locationName', nameValidation?.error, 'meeting-confirmation');
    }

    // Validate location address
    const addressValidation = validateLocationAddress(meetingData?.locationAddress);
    if (!addressValidation?.valid) {
      errors.locationAddress = addressValidation?.error;
      trackValidationError('locationAddress', addressValidation?.error, 'meeting-confirmation');
    }

    // Validate field types
    const statusTypeValidation = validateFieldType(meetingData?.status, 'string', 'Status');
    if (!statusTypeValidation?.valid) {
      errors.status = statusTypeValidation?.error;
      trackValidationError('status', statusTypeValidation?.error, 'meeting-confirmation');
    }

    return errors;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading meeting details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} className="text-destructive" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            {loadTimeout ? 'Loading Timeout' : 'Error'}
          </h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={handleRetryLoad} iconName="RefreshCw" iconPosition="left">
              Retry
            </Button>
            <Button variant="outline" onClick={() => navigate('/')}>Go to Home</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return null;
  }

  const selectedLocation = {
    id: meeting?.id,
    name: meeting?.locationName,
    address: meeting?.locationAddress,
    type: meeting?.locationType,
    lat: meeting?.locationLat,
    lng: meeting?.locationLng,
    rating: meeting?.locationRating,
    phone: meeting?.locationPhone,
    hours: meeting?.locationHours,
    image: meeting?.locationImage,
    imageAlt: meeting?.locationImageAlt,
    features: meeting?.locationFeatures || []
  };

  const currentUserParticipant = participants?.find(p => p?.userId === user?.id);
  const allConfirmed = participants?.length > 0 && participants?.every(p => p?.hasConfirmed);
  const confirmedCount = participants?.filter(p => p?.hasConfirmed)?.length;
  const totalCount = participants?.length;

  return (
    <>
      <Helmet>
        <title>Meeting Confirmation - Mosey</title>
        <meta name="description" content="Confirm your meeting details with location information, participant travel times, and booking options." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <StepProgressIndicator />
        <WorkflowBreadcrumb />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 lg:py-10 pb-32">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                allConfirmed ? 'bg-success/10' : 'bg-warning/10'
              }`}>
                <Icon 
                  name={allConfirmed ? 'CheckCircle' : 'Clock'} 
                  size={24} 
                  className={allConfirmed ? 'text-success' : 'text-warning'} 
                />
              </div>
              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
                  Meeting Confirmation
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {allConfirmed 
                    ? '✓ All participants confirmed' 
                    : `${confirmedCount} of ${totalCount} participants confirmed`
                  }
                </p>
              </div>
            </div>

            {/* Validation Warnings */}
            {Object.keys(validationErrors)?.length > 0 && (
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-2">
                  <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-warning mb-2">Data Validation Warnings</h3>
                    <ul className="text-sm text-warning/90 space-y-1">
                      {Object.entries(validationErrors)?.map(([field, error]) => (
                        <li key={field}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6 md:space-y-8">
            <LocationDetails location={selectedLocation} />
            
            <ParticipantTravelDetails 
              participants={participants}
              meetingTime={meeting?.meetingTime}
              currentUserId={user?.id}
            />

            <BookingActions 
              location={selectedLocation}
              participants={participants}
              meetingDate={meeting?.meetingDate}
              meetingTime={meeting?.meetingTime}
            />
          </div>
        </main>

        <NavigationActions
          onPrevious={() => navigate(-1)}
          previousLabel="Back to Filters"
          onNext={() => {}}
        />
      </div>
      <ToastContainer />
    </>
  );
};

export default MeetingConfirmation;